/**
 * Normalized HTTP error categories used across the accounts panel.
 */
export enum HttpErrorType {
  /** HTTP status 404 — token not found or expired. */
  InvalidToken = 'INVALID_TOKEN',
  /** HTTP status 401 — caller not authorized. */
  Unauthorized = 'UNAUTHORIZED',
  /** HTTP status 5xx — backend failure. */
  ServerError = 'SERVER_ERROR',
  /** HTTP status 0 — offline or CORS/network failure. */
  Network = 'NETWORK',
  /** Any other non-success status. */
  Unknown = 'UNKNOWN',
}

/**
 * Application-level HTTP error thrown by the global HTTP error interceptor.
 *
 * @example
 * ```ts
 * catchError((err) => {
 *   if (err instanceof AppHttpError && err.type === HttpErrorType.InvalidToken) {
 *     // show invalid token message
 *   }
 * });
 * ```
 */
export class AppHttpError extends Error {
  /**
   * @param type - Mapped error category for UI messaging.
   * @param status - Original HTTP status code from the response.
   */
  constructor(
    public readonly type: HttpErrorType,
    public readonly status: number,
  ) {
    super(`HTTP error: ${status}`);
    this.name = 'AppHttpError';
  }
}

/**
 * Maps an HTTP status code to a stable {@link HttpErrorType} for the UI layer.
 *
 * @param status - HTTP status from {@link HttpErrorResponse}.
 * @returns Corresponding error category.
 *
 * @example
 * ```ts
 * mapStatusToHttpErrorType(404); // HttpErrorType.InvalidToken
 * mapStatusToHttpErrorType(0);   // HttpErrorType.Network
 * ```
 */
export function mapStatusToHttpErrorType(status: number): HttpErrorType {
  if (status === 0) {
    return HttpErrorType.Network;
  }
  if (status === 401) {
    return HttpErrorType.Unauthorized;
  }
  if (status === 404) {
    return HttpErrorType.InvalidToken;
  }
  if (status >= 500) {
    return HttpErrorType.ServerError;
  }
  return HttpErrorType.Unknown;
}
